import { Button, Flex, Spinner, Tooltip, useToast } from '@chakra-ui/react';
import axios from 'axios';
import FileSaver from 'file-saver';
import JSZip from 'jszip';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { funding as calcAgentFunding } from '@finley-technologies/calc-agent';
import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Reporting } from '@finley-technologies/finley-types';

import { Icon } from 'packages/common-frontend/components/icons';
import { Typography } from 'packages/common-frontend/components/Typography';
import {
  errorToastConfig,
  successToastConfig,
} from 'packages/common-frontend/constants';
import { fileExtensionToMimeTypeMap } from 'packages/common-frontend/utils';
import { useGetCurrentCreditAgreementFullArtifactQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useFundingRequestTemplateContextV1 } from 'packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { generatePreviewFundingNoticeWorklease } from 'packages/funding-requests/ui/submit/DownloadPreviewFundingNoticeButton';
import {
  useGetReportS3DownloadUrlsByReportingWorkleaseRecordIdsLazyQueryGQLType,
  useProduceReportingGenerateMessagesMutation,
} from 'packages/reporting/requests/reporting.gql.generated';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';

// returns the correct file extension for a Blob which is required for files opening correctly when unzipping
const getFileExtensionFromBlob = (blob: Blob): string | undefined => {
  return Object.entries(fileExtensionToMimeTypeMap).find(
    ([_extension, mimeType]) => mimeType === blob.type,
  )?.[0];
};

type DownloadAllFundingNoticesButtonBodyParams = {
  reportName: string;
  lenderShareSubmissions: calcAgentFunding.DrawRequestSubmissionLenderShare[];
};
const DownloadAllFundingNoticesButtonBody = ({
  reportName,
  lenderShareSubmissions,
}: DownloadAllFundingNoticesButtonBodyParams) => {
  const toast = useToast();
  const { user } = useUserAuthOrThrow();

  const fundingRequestContext = useFundingRequestTemplateContextV1();

  const [
    currentPollingWorkleaseRecordIds,
    setCurrentPollingWorkleaseRecordIds,
  ] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [produceGenerateMessages] = useProduceReportingGenerateMessagesMutation(
    {
      fetchPolicy: 'no-cache',
    },
  );

  const [
    getReportS3DownloadUrlsByWorkleaseIds,
    { data: reportS3DownloadUrlsByWorkleaseIdsData },
  ] = useGetReportS3DownloadUrlsByReportingWorkleaseRecordIdsLazyQueryGQLType({
    pollInterval: currentPollingWorkleaseRecordIds.length ? 1000 : 0, // poll every second if 'currentPollingWorkleaseRecordIds' exists
    fetchPolicy: 'no-cache',
  });

  const generatePreviewFundingNotices = useCallback(async () => {
    toast({
      ...successToastConfig,
      title:
        'Generating all preview funding notices. The files will be downloaded when ready.',
    });

    setIsLoading(true);

    const capitalProviderOrganizationIds: Set<string> = new Set();
    for (const lenderShareSubmission of lenderShareSubmissions) {
      capitalProviderOrganizationIds.add(
        lenderShareSubmission.lender.organizationId,
      );
    }

    // only need to generate one consolidated funding notice preview worklease per capital provider organization
    const workleases: Reporting.FundingNoticeReportingGenerateQaWorklease[] = Array.from(
      capitalProviderOrganizationIds,
    ).map((capitalProviderOrganizationId) =>
      generatePreviewFundingNoticeWorklease({
        fundingRequestContext,
        reportName,
        userOrganizationId: user.organizationId,
        capitalProviderOrganizationId,
        setIsLoading,
      }),
    );

    const { data } = await produceGenerateMessages({
      variables: {
        workleases,
      },
    });
    finleyAssert(
      data?.result,
      'Expecting a result from "produceGenerateMessages".',
    );

    const workleaseRecordIds = data.result.map(
      (worklease) => worklease.recordId,
    );

    setCurrentPollingWorkleaseRecordIds(workleaseRecordIds);

    getReportS3DownloadUrlsByWorkleaseIds({
      variables: {
        reportConfigType: 'fundingNotice',
        reportingWorkleaseRecordIds: workleaseRecordIds,
      },
    });
  }, [
    produceGenerateMessages,
    getReportS3DownloadUrlsByWorkleaseIds,
    fundingRequestContext,
    reportName,
    user.organizationId,
    toast,
    lenderShareSubmissions,
  ]);

  useEffect(() => {
    const handleWorkleaseStatuses = async () => {
      if (
        !reportS3DownloadUrlsByWorkleaseIdsData?.getReportS3DownloadUrlsByReportingWorkleaseRecordIds
      ) {
        return;
      }

      const workleaseResults =
        reportS3DownloadUrlsByWorkleaseIdsData.getReportS3DownloadUrlsByReportingWorkleaseRecordIds;

      const isAnyWorkleaseInErrorState = workleaseResults.some(
        (workleaseResult) => workleaseResult.workleaseStatus === 'error',
      );

      // if any worklease is in error state, toast error and stop polling
      if (isAnyWorkleaseInErrorState) {
        const workleaseErrors: string = workleaseResults
          .map((workleaseResult) => workleaseResult.error)
          .join(', ');

        const errorToastDescription =
          commonUtils.EnvUtils.isCustomerFacing() || !workleaseErrors
            ? undefined
            : workleaseErrors;

        toast({
          ...errorToastConfig,
          title: `Failed to retrieve valid download URLs [report:${reportName}]`,
          description: errorToastDescription,
        });

        setIsLoading(false);
        setCurrentPollingWorkleaseRecordIds([]);
        return;
      }

      const isAnyWorkleaseInInitializedState: boolean = workleaseResults.some(
        (workleaseResult) => workleaseResult.workleaseStatus === 'initialized',
      );

      // if any worklease in the collection is in initialized state, return and continue polling
      if (isAnyWorkleaseInInitializedState) {
        return;
      }

      const areAllWorkleasesInSuccessState = workleaseResults.every(
        (workleaseResult) => workleaseResult.workleaseStatus === 'success',
      );

      // if workleases are all in success state
      // stop polling, fetch files from s3, zip them all together, and download
      if (areAllWorkleasesInSuccessState) {
        setCurrentPollingWorkleaseRecordIds([]);

        const zip = new JSZip();
        try {
          await Promise.all(
            workleaseResults.map(async (workleaseResult, index) => {
              if (workleaseResult.s3DownloadUrl) {
                const response = await axios({
                  url: workleaseResult.s3DownloadUrl,
                  method: 'GET',
                  responseType: 'blob',
                });

                const fileBlob = new Blob([response.data], {
                  type: response.data.type,
                });
                const singleFileName = `${reportName}-preview-${commonDate.currentDateStr()}`;
                const singleFileExtension = getFileExtensionFromBlob(fileBlob);

                const fileNameToZip = singleFileExtension
                  ? `${singleFileName}-${index + 1}.${singleFileExtension}`
                  : `${singleFileName}-${index + 1}`;

                zip.file(fileNameToZip, fileBlob);
              }
            }),
          );

          const zippedFileBlob = await zip.generateAsync({ type: 'blob' });
          const zippedFileName = `${reportName}-preview-${commonDate.currentDateStr()}`;

          FileSaver.saveAs(zippedFileBlob, zippedFileName);
        } catch (error) {
          errorToastConfig.title = commonUtils.EnvUtils.isCustomerFacing()
            ? 'Failed to retrieve report preview. Please contact Finley for support.'
            : JSON.stringify(error);

          toast(errorToastConfig);
        } finally {
          setIsLoading(false);
        }
      }
    };

    if (currentPollingWorkleaseRecordIds.length) {
      // if there are workleases we are currently polling for,
      // handle the worklease results
      handleWorkleaseStatuses();
    }
  }, [
    reportName,
    toast,
    currentPollingWorkleaseRecordIds,
    reportS3DownloadUrlsByWorkleaseIdsData,
  ]);

  return (
    <Tooltip label="Generate and download all notice previews">
      <Button
        variant="unstyled"
        onClick={async () => {
          await generatePreviewFundingNotices();
        }}
        fontWeight={400}
        isDisabled={isLoading}
      >
        {isLoading ? (
          <Flex alignItems="center" gap="8px">
            <Spinner />
            <Typography>Generating Previews</Typography>
          </Flex>
        ) : (
          'Generate All Funding Notice Previews'
        )}
      </Button>
    </Tooltip>
  );
};

type DownloadAllPreviewFundingNoticesButtonParams = {
  lenderShareSubmissions: calcAgentFunding.DrawRequestSubmissionLenderShare[];
  creditFacilityId: string;
};
export const DownloadAllPreviewFundingNoticesButton = ({
  lenderShareSubmissions,
  creditFacilityId,
}: DownloadAllPreviewFundingNoticesButtonParams) => {
  const toast = useToast();
  const {
    data: creditAgreementData,
  } = useGetCurrentCreditAgreementFullArtifactQuery({
    onError: () => {
      errorToastConfig.title =
        'An unexpected error occurred when fetching the current credit agreement. Please contact Finley for support.';

      toast(errorToastConfig);
    },
    variables: {
      creditFacilityId,
    },
    fetchPolicy: 'no-cache',
  });

  const creditAgreement = useMemo(
    () =>
      parseCreditAgreementFullArtifact(
        creditAgreementData?.currentCreditAgreementFullArtifact
          ?.creditAgreement,
      ),
    [creditAgreementData],
  );

  const reportName = useMemo(() => {
    const reportingProperty = creditAgreement?.reportingMechanics.find(
      (reportingMechanic) =>
        reportingMechanic.reportConfigTypes.includes('fundingNotice'),
    );
    return reportingProperty?.name;
  }, [creditAgreement?.reportingMechanics]);

  if (!reportName) {
    return (
      <Tooltip label="Funding notices are not configured">
        <Button isDisabled variant="v2-ghost">
          <Icon icon="arrow-down-to-bracket" />
        </Button>
      </Tooltip>
    );
  }

  return (
    <DownloadAllFundingNoticesButtonBody
      reportName={reportName}
      lenderShareSubmissions={lenderShareSubmissions}
    />
  );
};
